import clsx from 'clsx';
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { US_STATE_CODES } from '../../utils/formValidation/constants';
import { TextField } from './TextField';

type StateDropdownProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string | boolean;
  className?: string;
};

// Convert Set to Array for easier iteration
const US_STATES = Array.from(US_STATE_CODES).sort();

export const StateDropdown: React.FC<StateDropdownProps> = ({
  value,
  onChange,
  placeholder = 'State',
  error = false,
  className,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filter states based on input
  const filteredStates = useMemo(() => {
    if (value) {
      const filtered = US_STATES.filter((state) => state.toLowerCase().includes(value.toLowerCase()));
      return filtered.length > 0 ? filtered : US_STATES;
    }
    return US_STATES;
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFocus = () => {
    setShowDropdown(true);
    setHighlightedIndex(filteredStates.length ? 0 : -1);
  };

  const handleSelect = (state: string) => {
    onChange(state);
    setShowDropdown(false);
  };

  return (
    <div ref={dropdownRef} className={clsx('relative flex-shrink-0', className)}>
      <TextField
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        error={typeof error === 'string' ? !!error : !!error}
        onFocus={handleFocus}
      />
      {showDropdown && (
        <div className="state-dropdown-menu">
          {filteredStates.length ? (
            filteredStates.map((state, idx) => (
              <div
                key={state}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(state);
                }}
                onMouseEnter={() => setHighlightedIndex(idx)}
                className={clsx('state-dropdown-item', idx === highlightedIndex ? 'highlighted' : '')}
              >
                {state}
              </div>
            ))
          ) : (
            <div className="state-dropdown-no-matches">No matches</div>
          )}
        </div>
      )}
    </div>
  );
};
